import { useState, useEffect } from 'react';

import Toggle from '@klv/molecules/toggle';
import { useTranslation } from 'react-i18next';
import { hasStorageAvailable } from '@klv/shared/utils/storage';
import { gtmFormEvent } from '@klv/shared/utils/google-tag-manager';
import { generateClassName } from '@klv/shared/utils/functions';
import { Formik, Form, useFormikContext } from 'formik';
import type { IRoiCalculatorForm, IInitialFormValues, IFormValidationSchema } from './types';
import * as Yup from 'yup';
import RoiInput from '../roi-input';
import { calculateRoiEmail, calculateRoiEmailAndSms, getInitialRoiFormValues } from '../../utils';
import Button from '@klv/atoms/button';
import * as styles from './styles/roiForm.module.scss';

// This is needed to be able to set the totals back to zero when the form changes
const FormObserver = ({ setCalculatedRoi, calculatedRoi }: IRoiCalculatorForm) => {
  const { values } = useFormikContext<IInitialFormValues>();

  useEffect(() => {
    if (calculatedRoi?.total) {
      setCalculatedRoi({
        total: '',
        subscriberRevenue:'',
        campaignsRevenue:'',
        flowsRevenue:'', 
        activeProfiles: '',
      });
    }
  }, [values]);

  return null;
};

const RoiForm = ({ 
  setCalculatedRoi,
  calculatedRoi,
}: IRoiCalculatorForm) => {
  const [emailOnly, setEmailOnly] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const [initialVals, setInitialVals] = useState({
    activeProfiles: '',
    webVisitors: '',
    averageOrderValue: '',
    averageNumberOfOrders: '',
    campaignsPerMonth: '',
    flowsPerMonth: '',
    smsPerMonth: '',
  });
  const initialValues: IInitialFormValues = initialVals;
  const { t } = useTranslation();
  const validationSchemaYupObject: IFormValidationSchema = {
    activeProfiles: Yup.number().min(0, 'Must be a positive #').required('A # is required'),
    webVisitors: Yup.number().min(0, 'Must be a positive #').required('A # is required'),
    averageOrderValue: Yup.number().min(0, 'Must be a positive #').required('A dollar amount is required'),
    averageNumberOfOrders: Yup.number().min(0, 'Must be a positive #').required('A # is required'),
    campaignsPerMonth: Yup.number().min(0, 'Must be a positive #').required('A # is required'),
    flowsPerMonth: Yup.number().min(0, 'Must be a positive #').required('A # is required'),
  };

  useEffect(() => {
    const localStorageValues = getInitialRoiFormValues();
    if (localStorageValues) {
      setEmailOnly(localStorageValues?.emailOnly);
      setInitialVals(localStorageValues?.formVals);
      // Defer setting calculatedRoi to avoid the form observer overwriting the value. with 1ms delay to push the execution to the end of the event loop.
      setTimeout(() => {setCalculatedRoi(localStorageValues?.calculatedRoi);}, 1);
    }
  }, []);
  if (!emailOnly) {
    validationSchemaYupObject.smsPerMonth = Yup.number().min(0, 'Must be a positive #').required('A # is required');
  }

  return (
        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={Yup.object(validationSchemaYupObject)}
          validateOnBlur={false}
          onSubmit={(values) => {
            if (emailOnly) {
              const newCalculatedRoi = calculateRoiEmail({ webVisitors: values?.webVisitors ? Number(values?.webVisitors)  : 0,
                activeProfiles: values?.activeProfiles ? Number(values?.activeProfiles)  : 0,
                averageOrderSize: values?.averageOrderValue ? Number(values?.averageOrderValue)  : 0,
                ordersPerMonth: values?.averageNumberOfOrders ? Number(values?.averageNumberOfOrders)  : 0,
                flowsPerMonth: values?.flowsPerMonth ? Number(values?.flowsPerMonth)  : 0,
              });
              setCalculatedRoi(newCalculatedRoi);
            } else {
              const newCalculatedRoi = calculateRoiEmailAndSms({ webVisitors: values?.webVisitors ? Number(values?.webVisitors)  : 0,
                activeProfiles: values?.activeProfiles ? Number(values?.activeProfiles)  : 0,
                averageOrderSize: values?.averageOrderValue ? Number(values?.averageOrderValue)  : 0,
                ordersPerMonth: values?.averageNumberOfOrders ? Number(values?.averageNumberOfOrders)  : 0,
                flowsPerMonth: values?.flowsPerMonth ? Number(values?.flowsPerMonth)  : 0,
                smsPerMonth: values?.flowsPerMonth ? Number(values?.smsPerMonth)  : 0,
              });
              setCalculatedRoi(newCalculatedRoi);
            }
            if (hasStorageAvailable('localStorage')) {
              localStorage.setItem('roiFormValues', JSON.stringify({ 
                activeProfiles: values.activeProfiles,
                webVisitors: values.webVisitors,
                averageOrderValue: values.averageOrderValue,
                averageNumberOfOrders: values.averageNumberOfOrders,
                campaignsPerMonth: values.campaignsPerMonth,
                flowsPerMonth: values.flowsPerMonth,
                smsPerMonth: values.smsPerMonth,
                emailOnly: emailOnly,
              }));
            }
            gtmFormEvent(null, 'roiCalculator', 'submit');
          }}
        >
        {(formik) => {
          return (
            <Form id="form--roiCalculator"
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                formik.submitForm();
              }
            }}
            >
            <FormObserver setCalculatedRoi={setCalculatedRoi} calculatedRoi={calculatedRoi}/>
            <div className={generateClassName(['grid', styles.formContainer, styles.formContainerLower])}>
              <h6 className={generateClassName(['h6', styles.brandText])}>{t('roiCalculator_form_brandText')}</h6>
              <p className={generateClassName(['paragraph3', styles.upperRequiredText])}>{t('roiCalculator_form_fieldsRequired')}</p>
            </div>
            <div className={generateClassName(['grid', styles.formContainer, styles.formContainerUpper])}>
            <div className={styles.toggleContainer}>
              <p className={generateClassName(['paragraph3', styles.toggleLabel])}>{t('roiCalculator_form_channelsUsed')}</p>
                <Toggle
                  className={styles.smsToggle}
                  backgroundColor='white'
                  toggleColor='lavender'
                  wide
                  falseText={t('roiCalculator_form_toggleEmailAndSms')}
                  setValue={() => setEmailOnly(email => !email)}
                  value={emailOnly}
                  trueText={t('roiCalculator_form_toggleEmail')}
                  variant="short"
                />
              </div>
              <div className={styles.emailAndWebContainer}>
              <RoiInput
                inputClassName={styles.input}
                id="roiForm-emailSize"
                label={t('roiCalculator_form_inputActiveProfiles')}
                name="activeProfiles"
                type="number"
                placeholder={t('roiCalculator_form_inputEnterActiveProfiles')}
                fieldGroupClassName={styles.orderFieldGroup}
                submitted={submitted}
                labelClassName={styles.textLabel}
                errorClassName={styles.textError}
                step="any"
              />
              <RoiInput
                inputClassName={styles.input}
                id="roiForm-webVisitors"
                label={t('roiCalculator_form_inputMonthlyWebVisitors')}
                name="webVisitors"
                type="number"
                placeholder={t('roiCalculator_form_inputEnterMonthlyWebVisitors')}
                fieldGroupClassName={styles.orderFieldGroup}
                submitted={submitted}
                labelClassName={styles.textLabel}
                errorClassName={styles.textError}
                step="any"
              />
            </div>
            </div>
            <div className={generateClassName(['grid', styles.formContainer, styles.formContainerLower])}>
              <div className={styles.orderValueContainer}>
                <p className={generateClassName(['paragraph2', styles.inputPrefix])}>{t('roiCalculator_form_inputPrefix')}</p>
                <RoiInput
                  inputClassName={`${styles.input} ${styles.orderValueInput}`}
                  id="roiForm-orderValue"
                  name="averageOrderValue"
                  label={t('roiCalculator_form_inputAverageOrderValue')}
                  type="number"
                  placeholder={t('roiCalculator_form_inputEnterAverageOrderValue')}
                  submitted={submitted}
                  labelClassName={`${styles.textLabel} ${styles.orderLabel}`}
                  errorClassName={`${styles.textError} ${styles.orderError}`}
                  fieldGroupClassName={styles.orderFieldGroup}
                  step="any"
                />
              </div>
              <RoiInput
                inputClassName={styles.input}
                id="roiForm-averageOrders"
                label={t('roiCalculator_form_inputOrdersPerMonth')}
                name="averageNumberOfOrders"
                type="number"
                placeholder={t('roiCalculator_form_inputEnterOrdersPerMonth')}
                submitted={submitted}
                labelClassName={styles.textLabel}
                fieldGroupClassName={styles.orderFieldGroup}
                errorClassName={styles.textError}
                step="any"
              />
              <RoiInput
                inputClassName={styles.input}
                id="roiForm-campaignsPerMonth"
                label={t('roiCalculator_form_inputCampaignsPerMonth')}
                name="campaignsPerMonth"
                type="number"
                placeholder={t('roiCalculator_form_inputEnterCampaignsPerMonth')}
                submitted={submitted}
                labelClassName={styles.textLabel}
                fieldGroupClassName={styles.orderFieldGroup}
                errorClassName={styles.textError}
                step="any"
              />
              <RoiInput
                inputClassName={styles.input}
                id="roiForm-flowsPerMonth"
                label={t('roiCalculator_form_inputFlowsPerMonth')}
                name="flowsPerMonth"
                type="number"
                placeholder={t('roiCalculator_form_inputEnterFlowsPerMonth')}
                submitted={submitted}
                labelClassName={styles.textLabel}
                fieldGroupClassName={styles.orderFieldGroup}
                errorClassName={styles.textError}
                step="any"
              />
              {!emailOnly && <RoiInput
                inputClassName={styles.input}
                id="roiForm-smsPerMonth"
                label={t('roiCalculator_form_inputSmsPerMonth')}
                name="smsPerMonth"
                type="number"
                placeholder={t('roiCalculator_form_inputEnterSmsPerMonth')}
                submitted={submitted}
                labelClassName={styles.textLabel}
                fieldGroupClassName={styles.orderFieldGroup}
                errorClassName={styles.textError}
                step="any"
              />}
              <p className={generateClassName(['paragraph3', styles.lowerRequiredText])}>{t('roiCalculator_form_fieldsRequired')}</p>
            </div>
            <Button
              className={styles.button}
              text={t('roiCalculator_form_roiButton')}
              buttonColor="dark"
              variant="primary"
              type="submit"
              onFocus={() => setSubmitted(true)}
            />
          </Form>
          );
        }}
      </Formik>
  );
};

export default RoiForm;
